import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { Event as GlobalEvent } from "../../../Global/Base/Events";
import { Key } from "../../../Global/Base/Utils/EventUtils";
import { StringUtils } from "../../../Global/Base/Utils/StringUtils";
import { Control } from "../../UI.BaseControls/Control";
import { EventHelper } from "../../UI.BaseControls/Helpers/EventHelper";
import { PopupMenu } from "../../UI.BaseControls/PopupMenu";
import { ElementUtils } from "../../UI.ElementarControls/Helpers/ElementUtils";
import { registerCustomElement } from "../../UI.ElementarControls/Utils/CustomElementUtils";
import { IWFAction } from "../Actions/IAction";
import { INodeAction } from "../Actions/INodeAction";
import { DropDownMenuHorizontalOpen, DropDownMenuVerticalOpen, IDropDownMenu, IDropDownMenuItem, Orientation } from "./DropDownInterfaces";
import { DropDownMenuItem } from "./DropDownMenuItem";
import { DropDownMenuLoader } from "./DropDownMenuLoader";
import { DropDownMenuWithSubmenuItem } from "./DropDownMenuWithSubmenuItem";
import { SeparatorMenuItem } from "./SeparatorItem";

const logger = LazyLoadingSectionLogger.create(LoggingSection.dropDown);

/**
 * Třída reprezentující DropDownMenu
 */
export class DropDownMenu extends Control implements IDropDownMenu {
    //#region Events
    //public ItemAdded: Event<DropDownMenu, IDropDownMenuItem> = new Event<DropDownMenu, IDropDownMenuItem>();
    public onClose: GlobalEvent<DropDownMenu, any>;
    //ItemAdded, ItemRemoved
    //#endregion Events

    //#region Listeners
    private _windowResizeListener: EventListener;
    //#endregion Listeners

    //#region Properties
    /**
     * Orientace menu, bud vertikalni nebo horizontalni
     */
    public get orientation(): Orientation {
        return this._orientation;
    }

    public set orientation(val: Orientation) {
        this._orientation = val;

        if (this._orientation == Orientation.vertical)
            this.classList.add("Vertical");
        else if (this._orientation == Orientation.horizontal)
            this.classList.add("Horizontal");
    }

    public get horizontalOpenPosition(): DropDownMenuHorizontalOpen {
        return this._horizontalOpenPos;
    }

    public set horizontalOpenPosition(v: DropDownMenuHorizontalOpen) {
        this._horizontalOpenPos = v;
    }

    public get verticalOpenPosition(): DropDownMenuVerticalOpen {
        return this._verticalOpenPos;
    }

    public set verticalOpenPosition(v: DropDownMenuVerticalOpen) {
        this._verticalOpenPos = v;
    }

    public get itemsCount(): number {
        return this._items.size;
    }

    /**
     * NodeAction obsahujici seznam položek
     */
    public get nodeAction(): INodeAction {
        return this._nodeAction;
    }

    public set nodeAction(val: INodeAction) {
        this._nodeAction = val;
        this._parseItemByNodeAction();

        val.itemAdded.add(() => {
            this._parseItemByNodeAction();
        });
        val.itemRemoved.add(() => {
            this._parseItemByNodeAction();
        });
    }

    /**
     * Položka obsahuje kořenové menu (menu na začátku stromu)
     */
    public get rootMenu(): DropDownMenu {
        return this._rootMenu;
    }

    public set rootMenu(val: DropDownMenu) {
        this._rootMenu = val;
    }

    /**
     * Obsahuje aktuálně otevřené submenu menu
     */
    public get openedSubmenu(): DropDownMenu {
        return this._openedSubmenu;
    }

    public set openedSubmenu(val: DropDownMenu) {
        this._openedSubmenu = val;
        if (val) {
            this._openedSubmenu._lastOffsetShowLeft = this._lastOffsetShowLeft === 10 ? 30 : 10;
        }
    }

    /**
     * Nastavuje se v pripade ze menu pretika pres obrazovku, tak zobrazi sipky
     */
    public get isOverflow(): boolean {
        return this.classList.contains("overflow");
    }

    public set isOverflow(val: boolean) {
        //logger.log("Overflow", val);
        //this.classList.toggle("overflow", val);
        if (val) {
            this.classList.add("overflow");
            if (this.orientation === Orientation.vertical)
                this._popup.element.style.height = document.body.clientHeight + "px";
            else
                this._popup.element.style.width = document.body.clientWidth + "px";
        } else {
            this.classList.remove("overflow");
            this._popup.element.style.height = "auto";
        }
    }

    /**
     * Zda je menu otevřené
     */
    public get isOpened(): boolean {
        return this._isOpened;
    }

    /**
     * Zda obsahuje nejaky primary action button
     */
    public set hasPrimaryAction(value: boolean) {
        this._hasPrimaryAction = value;
        if (this.rootMenu) this.rootMenu.hasPrimaryAction = value;
    }
    public get hasPrimaryAction(): boolean {
        if (this.rootMenu) return this.rootMenu.hasPrimaryAction;
        else return this._hasPrimaryAction;
    }
    //#endregion Properties

    //#region Fields
    public lastSelectedItem: IDropDownMenuItem;
    public closeTimeout: number = null;
    public disableCloseTimeout: boolean;

    private _popup: PopupMenu;
    private _items: Set<IDropDownMenuItem>;
    private _nodeAction: INodeAction;
    private _priorityIndex: any;
    private _orientation: Orientation;
    private _rootMenu: DropDownMenu = null;
    private _lastOffsetShowLeft: number;
    private _openedSubmenu: DropDownMenu = null;
    private _isOpened: boolean = false;
    private _lastStartElement: HTMLElement | IDropDownMenuItem;
    private _lastOpenedTarget: HTMLElement;
    private _emptyDropDownMenu: DropDownMenu;
    private _horizontalOpenPos: DropDownMenuHorizontalOpen;
    private _verticalOpenPos: DropDownMenuVerticalOpen;
    private _contentElement: HTMLDivElement;
    private _upBtnElement: HTMLElement;
    private _downBtnElement: HTMLElement;
    private _filter: string = "";
    private _hasPrimaryAction: boolean = false;

    private static _lastOpenedDropdown: DropDownMenu;
    //#endregion Fields

    //#region Constructors
    protected override _constructor(): void {
        super._constructor();

        this.onClose = new GlobalEvent<DropDownMenu, any>();
        this.visible = false;
        this._items = new Set<IDropDownMenuItem>();
        this._priorityIndex = {};
        this._lastOffsetShowLeft = 10;
        this._orientation = Orientation.vertical;
        this.disableCloseTimeout = false;

        this.classList.add("DropDownMenu");
        this.classList.add("DesktopMenu");

        this._verticalOpenPos = DropDownMenuVerticalOpen.bottom;
        this._horizontalOpenPos = DropDownMenuHorizontalOpen.right;

        if (this._orientation == Orientation.vertical)
            this.classList.add("Vertical");
        else if (this._orientation == Orientation.horizontal)
            this.classList.add("Horizontal");

        this._createElement();
        this.setAttribute("tabindex", "1");

        /**
         * Menu je sdílené pro více řádků záznamů. Zobrazuje se ale v PopupMenu, které sdílené není a při zavření se odstraňuje z DOM.
         * Menu je v tu chvíli potomkem, takže by se disposovalo také, pokud by zůstal disposeOnDetach nastavený na true.
         */
        this.disposeOnDetach = false;
    }

    protected override connectedCallback(): void {
        super.connectedCallback();
        this._setListener();
    }
    //#endregion Constructors

    //#region Methods
    public filter(filterText: string): void {
        this._filter = StringUtils.removeAccents(filterText.trim());
        if (!filterText) {
            this.clearFilter();
            return;
        }
        const regExp = new RegExp(this._filter, "i");
        for (const item of this._items) {
            const it = item as DropDownMenuItem;
            const fvalue: string = it.filterLoc;
            if (fvalue == undefined)
                continue;

            if (regExp.test(fvalue)) {
                //it.HighlightCaption(fvalue);
                it.highlightCaption(this._filter);
                it.visible = true;
            } else {
                it.highlightCaption("");
                it.visible = false;
            }
        }
    }

    public clearFilter(): void {
        for (const item of this._items) {
            const it = item as DropDownMenuItem;
            it.visible = true;
            it.highlightCaption("");
        }
    }

    public addItem(item: IDropDownMenuItem): void {
        this._addItem(item);

        this._redraw();
        //this.ItemAdded.trigger(this, item);
        this.callEvent("ItemAdded", item);
    }

    /**
     * Přidání nového DropDownMenuItemu za určený existující
     * @param itemToAdd Item k přidání
     * @param existingItem Existující item
     */
    public addItemAfter(itemToAdd: IDropDownMenuItem, existingItem: IDropDownMenuItem): void {
        if (!this._contentElement.contains(itemToAdd as HTMLElement)) {
            (existingItem as HTMLElement).insertAdjacentElement("afterend", itemToAdd);
            this._addItem(itemToAdd);
            this.callEvent("ItemAdded", itemToAdd);
            this._redraw();
        }
    }

    /**
     * Přidá položku na určitou pozici za určený index.
     * @param itemToAdd Přidávaná položka.
     * @param existingIndex Pořadí existující položky, za kterou se má nová přidat.
     */
    public addItemAfterIndex(itemToAdd: IDropDownMenuItem, existingIndex: number): void {
        if (existingIndex >= this._items.size) return;//index je mimo rozsah existujícího seznamu, nic nepřidávej
        let i: number = 0;
        for (const menuItem of this._items) {
            if (i === existingIndex) {
                this.addItemAfter(itemToAdd, menuItem);
                return;
            }
            i++;
        }
    }

    public removeItem(item: IDropDownMenuItem): void {
        this._remItem(item);

        this._redraw();
        //this.ItemAdded.trigger(this, item);
        this.callEvent("ItemRemoved", item);
    }

    private _addItem(item: IDropDownMenuItem): void {
        if (!this._priorityIndex[item.priority])
            this._priorityIndex[item.priority] = [];
        this._priorityIndex[item.priority].push(item);
        item.parent = this;
        this._items.add(item);
    }

    private _remItem(item: IDropDownMenuItem): void {
        this._items?.delete(item);

        const obj = Object.keys(this._priorityIndex);
        for (const priority_list of obj) {
            const items = this._priorityIndex[priority_list];
            const indexOfItem = items.indexOf(item);
            if (indexOfItem > -1)
                items.splice(indexOfItem, 1);
        }
    }

    public clearItems(): void {
        this._priorityIndex = {};

        this._items.clear();
    }

    private _getHeightRelative(): number {
        let h = 0;

        for (const i of this._items) {
            //logger.log("HR", (i as any).offsetWidth, (i as any).offsetHeight);
            const th = (i as any).offsetHeight;
            if (th != 0) {
                h += th;
                continue;
            }

            if (i instanceof SeparatorMenuItem) {
                h += 7;
            } else {
                h += 27;
            }
        }

        return h;
    }

    private _getWidthRelative(): number {
        let h = 0;

        for (const i of this._items) {
            const th = (i as any).offsetWidth;
            if (th != 0) {
                h += th;
                continue;
            }
            if (i instanceof SeparatorMenuItem) {
                h += 7;
            } else {
                h += 220;
            }
        }

        return h;
    }

    private _getMaxHeightOneItemRelative(): number {
        let h = 0;

        const w = this.getBoundingClientRect();
        if (w && w.height)
            return w.height;

        for (const i of this._items) {
            const th = (i as any).offsetHeight;
            if (th != 0 && th > h) {
                h = th;
                continue;
            }
        }

        if (h == 0)
            return 27;
        return h;
    }

    private _getMaxWidthOneItemRelative(): number {
        let h = 0;

        const w = this.getBoundingClientRect();
        if (w && w.width)
            return w.width;

        if (this._items) {
            for (const i of this._items) {
                const th = (i as any).offsetWidth;
                if (th != 0 && th > h) {
                    h = th;
                    continue;
                }
            }
        }

        if (h == 0)
            return 270;
        return h;
    }

    /**
     * Rozparsovani NodeAction a vytvoření položek do menu
     */
    private _parseItemByNodeAction(): void {
        this._items.clear();
        this._priorityIndex = {};

        for (const a of this._nodeAction.actions) {
            if (a instanceof INodeAction) {
                const d = new DropDownMenu();
                d.nodeAction = a;
                const item = new DropDownMenuWithSubmenuItem();
                item.action = a;
                if (a.locKey)
                    item.locKey = a.locKey;

                item.priority = a.priority;
                item.submenu = d;
                //this.addItem(new DropDownMenuWithSubmenuItem("", a.LocKey, a.Priority, d));
                this._addItem(item);
            } else {
                const item = new DropDownMenuItem();
                item.action = a;
                if (a instanceof IWFAction) {
                    item.setCaption(a.caption);
                }
                this._addItem(item);
            }
        }
        this._redraw();
    }

    protected _createElement(): void {
        this._contentElement = document.createElement("div");
        this._upBtnElement = document.createElement("div");
        this._downBtnElement = document.createElement("div");

        this._upBtnElement.classList.add("DropDownScrollUp", "disabled");
        this._downBtnElement.classList.add("DropDownScrollDown");
        this._contentElement.classList.add("DropDownMenuContent");
        this.appendChild(this._upBtnElement);
        this.appendChild(this._contentElement);
        this.appendChild(this._downBtnElement);
    }

    private _redraw(): void {
        //mazu pouze elementy, ktere nejsou v items, aby nedochazelo k detach udalosti
        for (let i = 0; i < this._contentElement.childNodes.length; i++) {
            const element: any = this._contentElement.childNodes[i];

            if (!this._items?.has(element)) {
                this._contentElement.removeChild(element);
                i--;
            }
        }

        const obj = Object.keys(this._priorityIndex);
        for (const priority_list of obj) {
            const items = this._priorityIndex[priority_list];
            for (const item of items) {
                if (!this._contentElement.contains(item))
                    this._contentElement.appendChild(item);
            }
        }
    }

    private _setListener(): void {
        const fn_scroll = (scroll: number) => {
            if (scroll < 0) {
                if (this.orientation == Orientation.vertical)
                    this._contentElement.scrollTop -= 50;
                else
                    this._contentElement.scrollLeft -= 100;
            } else {
                if (this.orientation == Orientation.vertical)
                    this._contentElement.scrollTop += 50;
                else
                    this._contentElement.scrollLeft += 100;
            }
            if (this.orientation == Orientation.vertical) {
                this._downBtnElement.classList.toggle("disabled", this._contentElement.scrollTop + this._contentElement.clientHeight >= (this._contentElement.scrollHeight - 3));
                this._upBtnElement.classList.toggle("disabled", this._contentElement.scrollTop == 0);
            } else {
                this._downBtnElement.classList.toggle("disabled", this._contentElement.scrollLeft + this._contentElement.clientWidth >= (this._contentElement.scrollWidth - 3));
                this._upBtnElement.classList.toggle("disabled", this._contentElement.scrollLeft == 0);
            }
        };
        this.registerEventListener("mouseenter", () => {
            if (this.closeTimeout)
                clearTimeout(this.closeTimeout);
            if (this.openedSubmenu && this.openedSubmenu.lastSelectedItem)
                (this.openedSubmenu.lastSelectedItem as any).classList.remove("selected");
        });

        this.registerEventListener("mouseleave", () => {
            if (this.openedSubmenu && this.openedSubmenu.lastSelectedItem)
                (this.openedSubmenu.lastSelectedItem as any).classList.add("selected");
        });

        this._eventsController.addListener(this._upBtnElement, "click", () => {
            fn_scroll(-1);
        });

        this._eventsController.addListener(this._downBtnElement, "click", () => {
            fn_scroll(1);
        });

        this._eventsController.addListener(this._contentElement, "wheel", (e: WheelEvent) => {
            fn_scroll(e.deltaY < 0 ? -1 : 1);
            //e.preventDefault();
        }, { options: { passive: true } });

        // všechny dropdowny zavíráme pokud došlo ke kliknutí někam do okna (i pokud došlo k zastavení této události - událost "clickCancelled")
        // Výjimkou je klik někam do menu nebo zobrazený message box
        const wclick = (e: MouseEvent | CustomEvent) => {
            if (this._lastStartElement == null || this.rootMenu != null || ElementUtils.inElement(this as HTMLElement, e)) {
                return;
            }

            let os = this.openedSubmenu;
            while (os) {
                if (ElementUtils.inElement(os as HTMLElement, e)) {
                    return;
                }
                os = os.openedSubmenu;
            }

            const clickedOnSameElement = this._lastStartElement instanceof HTMLElement && this._lastStartElement as HTMLElement === e.target;
            this.closeAll(clickedOnSameElement);
        };

        this._eventsController.addListener(window, "click", wclick);
        this._eventsController.addListener(window, "clickCancelled", wclick);

        this._eventsController.addListener(this, "keydown", (e: KeyboardEvent) => {
            if (e.key === Key.Escape) { //Escape
                this.closeLastOpenedDropDown();
                e.stopPropagation();
                if (this._lastOpenedTarget) {
                    // Je žádoucí po zavření vrátit focus zpět na element, který dropdown otevřel.
                    this._lastOpenedTarget.focus();
                }
            }
        });
        this._eventsController.addListener(window, "keydown", (e: KeyboardEvent) => {
            if (e.key === Key.Escape) { //Escape
                this.closeLastOpenedDropDown();
                e.stopPropagation();
            }
            if (this.isOpened && e.key === Key.Enter) {
                if (this.hasPrimaryAction) this.closeAll(true);
                else e.stopPropagation();
            }
        });
        this._eventsController.addListener(this, "keyup", (e: KeyboardEvent) => {
            if (this.isOpened && e.key === Key.Enter) {
                if (this.hasPrimaryAction) this.closeAll(true);
                else e.stopPropagation();
            }
        });
    }

    private _cleanupLastElement(resetLastElement: boolean = false): void {
        if (this._lastStartElement == null)
            return;

        if (!(this._lastStartElement instanceof DropDownMenuItem))
            (this._lastStartElement as HTMLElement).classList.remove("isActive");

        if (resetLastElement)
            this._lastStartElement = null;
    }

    /**
     * Zobrazi menu
     * @param startElement Uvodni element, ze ktereho se vypocitava zobrazovana poloha, pokud je null, bere se to z udalosti
     * @param event Udalost, ktera predchazi zobrazeni menu
     * @param focusFirstItem Zda zaměřit první prvek
     */
    public show(startElement: HTMLElement | DropDownMenuItem = null, event: Event, focusFirstItem: boolean = false): void {
        if (startElement == null) {
            logger.error("Element of origin must be passed");
            return;
        }

        if (event)
            EventHelper.stopPropagation(event);

        // druhé otevření stejného dialogu ho zavře (resp. zavře se kliknutí a znovu se neotvírá)
        if (this._lastStartElement === startElement && DropDownMenu._lastOpenedDropdown === this) {
            this._lastStartElement = null;
            DropDownMenu._lastOpenedDropdown = null;
            return;
        }

        if (this._items?.size === 0)
            return;

        this._popup = new PopupMenu(this, false, "auto", "auto", "0", "0");
        this._popup.element.style.overflow = "visible";
        this.visible = true;

        if (focusFirstItem) {
            for (const item of this._items) {
                if (item instanceof IDropDownMenuItem) {
                    item.focus();
                    break;
                }
            }
        } else {
            this.focus();
        }

        this.isOverflow = false;
        this._isOpened = true;
        this._lastStartElement = startElement;
        this._lastOpenedTarget = startElement;

        // jako poslední otevřené menu je potřeba si pamatovat pouze hlavní menu (Root) - podřízená menu se nezavírají klikem
        if (this.rootMenu == null && event != null)
            DropDownMenu._lastOpenedDropdown = this;

        // pokud obsahuje nejake otevrene submenu je pri znovu otevreni zavrene
        if (this.openedSubmenu) {
            this.openedSubmenu.close();
            this.openedSubmenu = null;
        }

        this.recalculatePosition();

        // při změně velikosti okna se přepočítá i pozice popupu, aby zůstal u svého elementu
        if (!this._windowResizeListener) {
            this._windowResizeListener = () => this.recalculatePosition();
            this._eventsController.addListener(window, "resize", this._windowResizeListener);
        }

        this.callEvent("open");

        if (this._popup.visible)
            this._popup.setVisibility(false);
    }

    /**
     * Zobrazí prázdné dropdownmenu s loaderem.
     * @param locKey Popisek k loaderu. Defaultní hodnota "Prosím čekejte"
     */
    public showEmpty(element: HTMLElement, e: Event, locKey: string = "GUI-638298"): void {
        if (this.isOpened)
            return;
        if (!this._emptyDropDownMenu)
            this._emptyDropDownMenu = this._renderEmpty(locKey);
        this._emptyDropDownMenu.show(element, e);
    }

    /**
     * Vytvoří menu s loaderem a popiskem
     * @param locKey Popisek k loaderu. Defaultní hodnota "Prosím čekejte"
     */
    private _renderEmpty(locKey: string = "GUI-638298"): DropDownMenu {
        const actionsMenu = new DropDownMenu();
        actionsMenu.classList.add("loading");
        actionsMenu.addItem(DropDownMenuLoader.createMenuLoader(locKey));
        return actionsMenu;
    }

    /**
     * Přepočítá pozici a nastaví ji popupu
     */
    public recalculatePosition(): void {
        if (!this.isOpened)
            return;

        const menuWidth = this.offsetWidth > 0 ? this.offsetWidth : this._getMaxWidthOneItemRelative();
        const menuHeight = this.parentElement && this.parentElement.offsetHeight ? this.parentElement.offsetHeight : this._getMaxHeightOneItemRelative();

        //#region TEMP Metoda pro kontrolu pozice pri otevreni
        const fn_checkposition_vertical = (l: number,
            t: number,
            w: number = 0,
            h: number = 0,
            parentElement: HTMLElement | DropDownMenuItem | Event = null): { left: number; top: number } => {
            if (w === 0)
                w = menuWidth;
            if (h === 0)
                h = menuHeight || this._getHeightRelative();

            const rt = {
                left: l,
                top: t,
            };
            // pokud presahuje pravy okraj obrazovky
            if ((rt.left + w) > document.body.clientWidth) {
                if (parentElement instanceof DropDownMenuItem) {
                    rt.left = rt.left - 2 * w + 3;
                }
            }

            // pokud presahuje levy okraj obrazovky
            if (rt.left < 0) {
                //rt.top += h - 3;
                rt.left = this._lastOffsetShowLeft; // == 10 ? 30 : 10;
            }

            // Zajistí, že vertikální menu horizontálně nikdy nepřeteče přes okraj obrazovky (např. filtry při příliš dlouhém názvu nebo změně velikosti okna)
            this.style.maxWidth = `calc(100vw - ${rt.left}px)`; // `

            // pokud by bylo zobrazeno moc dole
            if ((rt.top + h) > document.body.clientHeight) {
                rt.top = rt.top - h;
            }
            // pokud se nevleze na obrazovku ani dole ani nahore je overflow
            if (rt.top < 0) {
                rt.top = 0;
                if ((rt.top + h) > document.body.clientHeight)
                    this.isOverflow = true;
            }

            return rt;
        };
        //#endregion TEMP Metoda pro kontrolu pozice pri otevreni
        //#region TEMP Metoda pro kontrolu pozice pri otevreni
        const fn_checkposition_horizontal = (l: number, t: number, w: number = 0, h: number = 0):
            { left: number; top: number } => {
            if (w === 0)
                w = menuWidth || this._getWidthRelative();
            if (h === 0)
                h = menuHeight;

            const rt = {
                left: l,
                top: t,
            };
                // pokud presahuje pravy okraj obrazovky
            if ((rt.left + w) > document.body.clientWidth) {
                //rt.top += h;
                rt.left = rt.left - w + 3;
            }

            // pokud presahuje levy okraj obrazovky
            if (rt.left < 0) {
                rt.left = 0;
                if ((rt.left + w) > document.body.clientWidth)
                    this.isOverflow = true;
            }

            // pokud by bylo zobrazeno moc dole
            if ((rt.top + h) > document.body.clientHeight) {
                rt.top = rt.top - h;
            }

            // pokud se nevleze na obrazovku ani dole ani nahore je overflow
            if (t < 0) {
                rt.top = 0;
            }

            return rt;
        };
        //#endregion TEMP Metoda pro kontrolu pozice pri otevreni

        let top = "0";
        let left = "0";

        if (this._lastStartElement != null) {
            const startElement = this._lastStartElement;
            if (startElement instanceof DropDownMenuItem) {
                const rp = (startElement as HTMLElement).getBoundingClientRect();

                // Nastavovani pozice pokud je vertical nadrazeny prvek
                if (startElement.parent && startElement.parent.orientation === Orientation.vertical) {
                    const l = rp.left + rp.width - 3;
                    const t = rp.top;

                    const f = fn_checkposition_vertical(l, t, menuWidth, menuHeight, startElement);
                    left = f.left.toString() + "px";
                    top = f.top.toString() + "px";
                } else {
                    left = (rp.left).toString() + "px";
                    top = (rp.top + rp.height).toString() + "px";
                }
            } else {
                // pokud startElement neni jine dropdownmenu pridame mu tridu isActive
                (startElement as HTMLElement).classList.add("isActive");
                const rp = (startElement as HTMLElement).getBoundingClientRect();
                let l = rp.left + rp.width - menuWidth;
                const t = rp.top + rp.height;

                //#region Dodatečné určení startovací pozice dropdownmenu
                if (this._horizontalOpenPos === DropDownMenuHorizontalOpen.left) {
                    l = rp.left;
                } else if (this._horizontalOpenPos === DropDownMenuHorizontalOpen.center) {
                    l = rp.left - (rp.width / 2);
                } else if (this._horizontalOpenPos === DropDownMenuHorizontalOpen.right) {
                    l = rp.left + rp.width - menuWidth;
                }
                //#endregion Dodatečné určení startovací pozice dropdownmenu

                let f: { left: number; top: number };

                if (this.orientation === Orientation.vertical) {
                    f = fn_checkposition_vertical(l, t, menuWidth, menuHeight, (startElement as HTMLElement));
                } else
                    f = fn_checkposition_horizontal(l, t, menuWidth, menuHeight);

                left = f.left + "px";
                top = f.top + "px";
            }
        } else {
            if (event && event instanceof MouseEvent) {
                const l = event.pageX;
                const t = event.pageY;
                let f: { left: number; top: number };

                if (this.orientation === Orientation.vertical)
                    f = fn_checkposition_vertical(l, t, menuWidth, menuHeight, event);
                else
                    f = fn_checkposition_horizontal(l, t, menuWidth, menuHeight);

                left = f.left.toString() + "px";
                top = f.top.toString() + "px";
            }
        }

        this._popup.setIndent(top, left);
    }

    /**
     * Zavre akutalni a vsechny podrizene nabidky
     */
    public close(dontOpenOnNextClick: boolean = false): void {
        this._cleanupLastElement(!dontOpenOnNextClick);

        this._isOpened = false;
        if (this.openedSubmenu) {
            this.openedSubmenu.close();
            this.openedSubmenu = null;
        }
        if (this._popup) {
            this._popup.setVisibility(false);
            this._popup.dispose();
            this._popup = null;
        }
        if (this.lastSelectedItem)
            (this.lastSelectedItem as DropDownMenuItem).classList.remove("selected");

        if (this._windowResizeListener) {
            this._eventsController.removeListener(window, "resize", this._windowResizeListener);
            this._windowResizeListener = null;
        }

        this.lastSelectedItem = null;
        if (!dontOpenOnNextClick && DropDownMenu._lastOpenedDropdown === this) {
            DropDownMenu._lastOpenedDropdown = null;
        }

        this.callEvent("closed");
        this.visible = false;
    }

    /**
     * Zavře poslední otevřený DropDownMenu
     */
    public closeLastOpenedDropDown(): void {
        if (this.openedSubmenu) {
            this.openedSubmenu.close();
            this.openedSubmenu = null;
        } else {
            this.close();
        }
    }

    /**
     * Zavre cele menu od korenoveho elementu
     */
    public closeAll(dontOpenOnNextClick: boolean = false, focusLastOpenedTarget: boolean = false): void {
        // Jinak se volají všechny následné "onClose" události, i když žádné menu není zobrazené a pak to rozbíjí listenery kontextového menu
        if (!this._isOpened) {
            return;
        }

        if (this.rootMenu)
            this.rootMenu.close(dontOpenOnNextClick);
        else
            this.close(dontOpenOnNextClick);

        if (focusLastOpenedTarget && this._lastOpenedTarget) {
            // Je žádoucí po zavření vrátit focus zpět na element, který dropdown otevřel.
            this._lastOpenedTarget.focus();
        }

        this.onClose.trigger(this, null);
    }
    //#endregion Methods

    //#region dispose
    /**
     * Destruktor
     */
    public override dispose(): void {
        this.remove();

        this._items?.forEach((item) => item.dispose());
        this._items.clear();

        super.dispose();
    }

    //#endregion dispose
}

registerCustomElement("tsc-DropDownMenu", DropDownMenu);
